import React from "react";
import { Typography, Descriptions } from "antd";
import { formatUnit } from "../../utils/constants";
import { BoqSummaryLineItem } from "../../utils/groundworksBoqSummaryComputer";

const { Text } = Typography;

export interface BcCalculationResult {
  rateMultiplierCode: string | null;
  rateMultiplierDbRate: number | null;
  multiplierMm: number | null;
  multiplierM: number | null;
  // Plain numeric multiplier (e.g. Drainage's ratePerPlot÷dbRate ratio, retaining/geo walls'
  // net×N, Plot Service's apartments multiple) — 1 when there's nothing to apply. Distinct
  // from multiplierM (the mm→m conversion), though multiplierM feeds into this when set.
  multiplierFactor: number;
  extraCode: string | null;
  extraDbRate: number | null;
  rate: number;
  quantity: number;
  amount: number;
  steps: string[];
}

/**
 * Backend-style calculation for the SummaryKey modal's "BCCalculation" block, shared by
 * GroundworksCutFillBillSummary and FoundationsBillSummary. Mirrors how the API is expected
 * to derive the amount from item-library dbRates alone (not the already-computed `rate`/
 * `amount` those pages carry). Rows carrying a `rateMultiplierCode` (e.g. pileMat's combined
 * "ALP031A+GWALP004" resourceCode) split the combined resourceCode: the rateMultiplierCode's
 * own dbRate is scaled by the multiplier (mm → m, ÷1000) and the remaining code's dbRate is
 * added on flat, before multiplying by quantity. Rows without one just use resourceCode's own
 * dbRate × quantity. The composed rate is rounded to 2dp and quantity to a whole number (same
 * convention the callers' own Rate/Qty columns use) before the final multiply.
 */
export function computeBcCalculation(
  record: BoqSummaryLineItem,
  getDbRate: (code: string) => number,
): BcCalculationResult {
  const rateMultiplierCode = (record as unknown as { rateMultiplierCode?: string }).rateMultiplierCode;
  const resourceCode = record.resourceCode || '';
  const roundedQuantity = Math.round(record.quantity || 0);

  // "|"-joined rateMultiplierCode (e.g. Scaffold's Crush 6F2|Type1 combo) — unlike
  // the single-code case below, each of these codes has its OWN independently
  // converted thickness factor (already in metres, "|"-joined 1:1 on `multiplier` —
  // e.g. "GWALP004|GWALP006" paired with "0.100|0.50" — no further mm÷1000 here,
  // that conversion already happened upstream). Any other codes in the combined
  // resourceCode (Level & Compact / Geotextile Layer) contribute their own dbRate flat.
  if (rateMultiplierCode?.includes('|')) {
    const rmCodes = rateMultiplierCode.split('|').map((s) => s.trim().toUpperCase()).filter(Boolean);
    const rmFactors = String(record.multiplier ?? '').split('|').map((s) => parseFloat(s.trim()) || 0);
    const rmDbRates = rmCodes.map((c) => getDbRate(c));
    // Each code's own dbRate × its own factor is rounded to 2dp BEFORE summing
    // (not just the final combined rate) — matches the consolidated row's own
    // formula (`computeScaffoldSection`), which sums each component's rounded
    // rate rather than rounding the pre-sum total once.
    const rmContributions = rmDbRates.map((r, i) => parseFloat((r * (rmFactors[i] ?? 0)).toFixed(2)));
    const rmContribution = rmContributions.reduce((sum, c) => sum + c, 0);
    const extraCodes = resourceCode
      .split('+')
      .map((s) => s.trim().toUpperCase())
      .filter((c) => c && !rmCodes.includes(c));
    const extraDbRate = extraCodes.reduce((sum, c) => sum + getDbRate(c), 0);
    const rate = parseFloat((rmContribution + extraDbRate).toFixed(2));
    const amount = parseFloat((rate * roundedQuantity).toFixed(2));
    return {
      rateMultiplierCode: rmCodes.join('|'),
      rateMultiplierDbRate: rmContribution,
      multiplierMm: null,
      multiplierM: null,
      multiplierFactor: 1,
      extraCode: extraCodes.join('+') || null,
      extraDbRate,
      rate,
      quantity: roundedQuantity,
      amount,
      steps: [
        rmCodes
          .map((c, i) => `£${rmDbRates[i].toFixed(4)} (${c} dbRate) × ${(rmFactors[i] ?? 0).toFixed(3)}m = £${rmContributions[i].toFixed(2)} (rounded)`)
          .join(' + '),
        `£${rmContribution.toFixed(2)} + £${extraDbRate.toFixed(4)} (${extraCodes.join('+') || '—'} dbRate) = £${rate.toFixed(2)} rate`,
        `£${rate.toFixed(2)} × ${roundedQuantity.toLocaleString('en-GB')} qty = £${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      ],
    };
  }

  if (rateMultiplierCode) {
    const rmCode = rateMultiplierCode.trim().toUpperCase();
    const rmDbRate = getDbRate(rmCode);
    const extraCode = resourceCode
      .split('+')
      .map((s) => s.trim().toUpperCase())
      .filter((c) => c && c !== rmCode)
      .join('+');
    const extraDbRate = extraCode ? getDbRate(extraCode) : 0;
    const multiplierNum = parseInt(String(record.multiplier ?? ''), 10) || 0;
    const multiplierM = multiplierNum / 1000;
    const rate = parseFloat((rmDbRate * multiplierM + extraDbRate).toFixed(2));
    const amount = parseFloat((rate * roundedQuantity).toFixed(2));
    return {
      rateMultiplierCode: rmCode,
      rateMultiplierDbRate: rmDbRate,
      multiplierMm: multiplierNum,
      multiplierM,
      multiplierFactor: multiplierM,
      extraCode: extraCode || null,
      extraDbRate,
      rate,
      quantity: roundedQuantity,
      amount,
      steps: [
        `£${rmDbRate.toFixed(4)} (${rmCode} dbRate) × ${multiplierM.toFixed(3)} (${multiplierNum}mm ÷ 1000)  = £${(rmDbRate * multiplierM).toFixed(4)}`,
        `£${(rmDbRate * multiplierM).toFixed(4)} + £${extraDbRate.toFixed(4)} (${extraCode || '—'} dbRate) = £${rate.toFixed(2)} rate`,
        `£${rate.toFixed(2)} × ${roundedQuantity.toLocaleString('en-GB')} qty = £${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      ],
    };
  }

  // No rateMultiplierCode: a combined resourceCode has no split logic to apply, but any
  // `multiplier` on the row still does — the db side always carries the "bare" per-code
  // rate, and `multiplier` is what scales it up to the row's actual rate:
  //  - a string ending "mm" (e.g. "909mm" on concreteBlinding) is a m³→m² thickness
  //    conversion — scale by mm ÷ 1000.
  //  - a plain number (e.g. Drainage's ratePerPlot÷dbRate ratio, retaining/geo walls'
  //    net×N, Plot Service's apartments multiple) is applied as a straight factor.
  //  - absent → factor of 1 (no scaling).
  // "/"-joined codes (e.g. "GWALPDD018/GWALPDD019") are alternates — AVERAGE their dbRates.
  // "+"-joined codes (e.g. "GWALP013+RMC0001") each contribute their own dbRate flat — SUM
  // them. Either way the (possibly scaled) result is then multiplied by quantity. A single
  // code collapses to the same thing (sum/average of one).
  const isAverage = resourceCode.includes('/');
  const codes = resourceCode
    .split(isAverage ? '/' : '+')
    .map((c) => c.trim().toUpperCase())
    .filter(Boolean);
  const codeRates = codes.map((c) => getDbRate(c));
  const combinedDbRate = codes.length === 0
    ? (Number(record.rate) || 0)
    : isAverage
      ? codeRates.reduce((sum, r) => sum + r, 0) / codeRates.length
      : codeRates.reduce((sum, r) => sum + r, 0);
  const rawMultiplier = record.multiplier;
  const isMmMultiplier = typeof rawMultiplier === 'string' && /mm\s*$/i.test(rawMultiplier.trim());
  const multiplierNum = isMmMultiplier ? (parseInt(rawMultiplier as string, 10) || 0) : null;
  const multiplierM = multiplierNum !== null ? multiplierNum / 1000 : null;
  const multiplierFactor = multiplierM !== null
    ? multiplierM
    : (typeof rawMultiplier === 'number' && isFinite(rawMultiplier) ? rawMultiplier : 1);
  const dbRate = combinedDbRate * multiplierFactor;
  const rate = parseFloat(dbRate.toFixed(2));
  const amount = parseFloat((rate * roundedQuantity).toFixed(2));
  const combinedLabel = codes.length > 1
    ? isAverage
      ? `(${codes.map((c, i) => `£${codeRates[i].toFixed(4)} (${c})`).join(' + ')}) ÷ ${codes.length}`
      : codes.map((c, i) => `£${codeRates[i].toFixed(4)} (${c})`).join(' + ')
    : `£${combinedDbRate.toFixed(4)} (${resourceCode || '—'} dbRate)`;
  const dbRateStep = multiplierM !== null
    ? `${combinedLabel} × ${multiplierM.toFixed(3)} (${multiplierNum}mm ÷ 1000) = £${rate.toFixed(2)} rate`
    : multiplierFactor !== 1
      ? `${combinedLabel} × ${multiplierFactor} (multiplier) = £${rate.toFixed(2)} rate`
      : `${combinedLabel} = £${rate.toFixed(2)} rate`;
  return {
    rateMultiplierCode: null,
    rateMultiplierDbRate: null,
    multiplierMm: multiplierNum,
    multiplierM,
    multiplierFactor,
    extraCode: null,
    extraDbRate: null,
    rate,
    quantity: roundedQuantity,
    amount,
    steps: [
      dbRateStep,
      `£${rate.toFixed(2)} × ${roundedQuantity.toLocaleString('en-GB')} qty = £${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    ],
  };
}

export interface BcCalculationDetailsProps {
  record: BoqSummaryLineItem;
  getDbRate: (code: string) => number;
}

// "Field Name" from "fieldName" — used to label every raw key on the record generically,
// so the modal stays correct across the different producer shapes (BoqSummaryLineItem vs
// DrainageSummaryLineItem) without hand-picking a fixed field list.
function prettifyKey(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

// Shows every element's own content (e.g. `resourceCodes`' `{resourceCode, operator}`
// entries) rather than just a count — "every detail", not a summary of how many there are.
function formatRawValue(value: unknown): string {
  if (value === undefined || value === null || value === '') return '—';
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    return value.map((v) => (typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v))).join(', ');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * Renders every raw key/value on a persisted (or live, pre-save) SummaryKey
 * record, in declaration order — generic over the record shape (accepts
 * `BoqSummaryLineItem`, `DrainageSummaryLineItem`, or a Dynamic Bill Summary
 * extra item's own `DynamicBillSummaryLineItem`) so both the fixed-row
 * SummaryKey modal (`BcCalculationDetails` below) and every fixed Bill
 * Summary's ad-hoc-extra-item "Live Value" modal show the complete record
 * instead of each hand-picking its own subset of fields (which silently
 * dropped `plotMapping`/`amountType`/`amountText`/`category`/`key` etc. on the
 * extra-item side). `children` is dropped from the record itself (not just
 * filtered from display) so it's never part of the persisted SummaryKey's raw
 * fields — a section's own nested rows aren't part of that section's own key.
 */
export const SummaryKeyRawFields: React.FC<{ record: Record<string, unknown> }> = ({ record }) => {
  const { children: _children, ...recordWithoutChildren } = record;
  const entries = Object.entries(recordWithoutChildren);
  return (
    <div style={{ marginBottom: 12 }}>
      <Text strong>SummaryKey (all persisted fields):</Text>
      <Descriptions bordered size="small" column={1} style={{ marginTop: 8 }}>
        {entries.map(([key, value]) => (
          <Descriptions.Item key={key} label={prettifyKey(key)}>
            {formatRawValue(value)}
          </Descriptions.Item>
        ))}
      </Descriptions>
    </div>
  );
};

/** Renders the SummaryKey modal's "BCCalculation" block — see `computeBcCalculation` above. */
export const BcCalculationDetails: React.FC<BcCalculationDetailsProps> = ({ record, getDbRate }) => {
  const bc = computeBcCalculation(record, getDbRate);
  return (
    <>
      <SummaryKeyRawFields record={record as unknown as Record<string, unknown>} />
      <div style={{ marginTop: 8, padding: 8, background: '#eff6ff', borderRadius: 4 }}>
      <Text strong>BCCalculation (backend dbRate-only derivation):</Text>
      <Descriptions bordered size="small" column={1} style={{ marginTop: 8 }}>
        {bc.rateMultiplierCode && (
          <Descriptions.Item label="Rate Multiplier Code dbRate">
            £{bc.rateMultiplierDbRate!.toFixed(4)} ({bc.rateMultiplierCode})
          </Descriptions.Item>
        )}
        {bc.multiplierM !== null ? (
          <Descriptions.Item label="Multiplier">
            {bc.multiplierMm}mm ÷ 1000 = {bc.multiplierM.toFixed(3)}m
          </Descriptions.Item>
        ) : bc.multiplierFactor !== 1 && (
          <Descriptions.Item label="Multiplier">× {bc.multiplierFactor}</Descriptions.Item>
        )}
        {bc.rateMultiplierCode && (
          <Descriptions.Item label="Remaining Code dbRate">
            £{bc.extraDbRate!.toFixed(4)} ({bc.extraCode || '—'})
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Computed Rate (rounded 2dp)">£{bc.rate.toFixed(2)}</Descriptions.Item>
        <Descriptions.Item label="Quantity (rounded)">
          {bc.quantity.toLocaleString('en-GB')} {formatUnit(record.unit)}
        </Descriptions.Item>
        <Descriptions.Item label="Amount (rounded 2dp)">
          £{bc.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Descriptions.Item>
      </Descriptions>
      <div style={{ marginTop: 8 }}>
        {bc.steps.map((step, idx) => (
          <div key={idx}>
            <Text style={{ fontSize: 12, color: '#374151' }}>{step}</Text>
          </div>
        ))}
      </div>
      </div>
    </>
  );
};

export default BcCalculationDetails;
